import { fromEvent } from 'file-selector';
import {
  type ChangeEvent,
  type DragEvent,
  type MouseEvent,
  memo,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { useTrackViewedOnboardingUpload } from '@air/analytics';
import { FolderIcon, UploadIcon } from '@air/design-icons';
import { ONE_SECOND } from '@air/utilities';
import { cn } from '@air/utils-tailwind';

import blueStyles from '~/components/v2/components/workspace/onboarding-intro/walkthrough/onboardingIntroWalkthrough.module.css';
import { AnimatedOnboardingUploadAction } from '~/components/WorkspaceOnboardingForm/AnimatedOnboardingUploadAction';
import { toAnimatedOnboardingUploadFiles } from '~/components/WorkspaceOnboardingForm/toAnimatedOnboardingUploadFiles';
import { WorkspaceAnimatedOnboardingUploadProgress } from '~/components/WorkspaceOnboardingForm/WorkspaceAnimatedOnboardingUploadProgress';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';

export type WorkspaceAnimatedOnboardingUploadStepProps = {
  onConfirmPick: (files: readonly File[]) => void;
  onFilesSelected?: (files: readonly File[]) => void;
  onSkip: () => void;
};

/** Long enough to read the count and the names, short enough that the run does not stall. */
const UPLOAD_CONFIRM_SECONDS = 1.8;

const UPLOAD_CONFIRM_MS = UPLOAD_CONFIRM_SECONDS * ONE_SECOND;

export const WorkspaceAnimatedOnboardingUploadStep = memo(
  ({ onConfirmPick, onFilesSelected, onSkip }: WorkspaceAnimatedOnboardingUploadStepProps) => {
    const headingId = useId();
    const filesInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);
    const hasContinuedRef = useRef<boolean>(false);
    const onConfirmPickRef = useRef(onConfirmPick);
    onConfirmPickRef.current = onConfirmPick;
    const { trackViewedOnboardingUpload } = useTrackViewedOnboardingUpload();
    const { skipOnboardingUpload } = useFeatureFlags();
    const [pickedFiles, setPickedFiles] = useState<readonly File[] | undefined>();

    useEffect(() => {
      trackViewedOnboardingUpload({ category: 'agent_onboarding' });
    }, [trackViewedOnboardingUpload]);

    useEffect(() => {
      if (!pickedFiles?.length || hasContinuedRef.current) {
        return;
      }

      const timeout = window.setTimeout(() => {
        hasContinuedRef.current = true;
        onConfirmPickRef.current(pickedFiles);
      }, UPLOAD_CONFIRM_MS);

      return () => window.clearTimeout(timeout);
    }, [pickedFiles]);

    useEffect(() => {
      // `webkitdirectory` is not in React's input props; set it so Upload folder opens a directory picker.
      folderInputRef.current?.setAttribute('webkitdirectory', '');
      folderInputRef.current?.setAttribute('directory', '');
    }, []);

    const handleBrowseFiles = useCallback(() => {
      filesInputRef.current?.click();
    }, []);

    const handleFilesClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      filesInputRef.current?.click();
    }, []);

    const handleFolderClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      folderInputRef.current?.click();
    }, []);

    const handleSkip = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onSkip();
      },
      [onSkip],
    );

    const acceptFiles = useCallback(
      (files: readonly File[]) => {
        if (!files.length) {
          return;
        }

        onFilesSelected?.(files);
        setPickedFiles(files);
      },
      [onFilesSelected],
    );

    const handlePicked = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        // `input.files` is live, so copy the picks out before resetting the value — clearing it
        // first empties the same list and the pick reads as nothing selected.
        const files = event.target.files ? toAnimatedOnboardingUploadFiles([...event.target.files]) : [];
        event.target.value = '';

        if (!files.length) {
          return;
        }

        acceptFiles(files);
      },
      [acceptFiles],
    );

    const handleInputClick = useCallback((event: MouseEvent<HTMLInputElement>) => {
      event.stopPropagation();
    }, []);

    const handleDrop = useCallback(
      (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (pickedFiles) {
          return;
        }

        // `dataTransfer.files` is one empty entry for a dropped folder. `fromEvent` walks the directory.
        void fromEvent(event)
          .then((entries) => {
            const uploadFiles = toAnimatedOnboardingUploadFiles(
              entries.filter((entry): entry is File => entry instanceof File),
            );

            if (!uploadFiles.length) {
              return;
            }

            acceptFiles(uploadFiles);
          })
          .catch(() => undefined);
      },
      [acceptFiles, pickedFiles],
    );

    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    }, []);

    return (
      <div
        className="absolute inset-0 z-10 p-4 sm:p-6"
        data-disableselect="true"
        data-testid="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_STEP"
      >
        {/* Drop-anywhere frame contains nested buttons, so this cannot be a button. Keyboard users use Upload files. */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: nested buttons already provide keyboard access */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: nested buttons already provide keyboard access */}
        <div
          className={cn(
            'flex h-full w-full flex-col rounded-16 ads:border-ads-sky-9 border-2 border-sky-9 border-dashed px-10 py-8 transition-colors',
            pickedFiles
              ? 'cursor-default'
              : 'cursor-pointer ads:hover:border-ads-altitude-11 hover:border-altitude-11 ads:hover:bg-ads-sky-4/20 hover:bg-sky-4/20',
          )}
          onClick={pickedFiles ? undefined : handleBrowseFiles}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            className="hidden"
            data-testid="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_FILE_INPUT"
            multiple
            onChange={handlePicked}
            onClick={handleInputClick}
            ref={filesInputRef}
            type="file"
          />
          <input
            className="hidden"
            data-testid="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_FOLDER_INPUT"
            multiple
            onChange={handlePicked}
            onClick={handleInputClick}
            ref={folderInputRef}
            type="file"
          />
          <div className="flex flex-1 flex-col items-center justify-center gap-6">
            {pickedFiles ? (
              <WorkspaceAnimatedOnboardingUploadProgress fileNames={pickedFiles.map((file) => file.name)} />
            ) : (
              <>
                <span
                  className={cn(
                    blueStyles.agentBlue,
                    blueStyles.agentBlueDrift,
                    'flex size-16 items-center justify-center rounded-full text-white',
                  )}
                  data-theme="light"
                >
                  <UploadIcon size="lg" />
                </span>
                <div aria-live="polite" className="flex flex-col items-center gap-3 text-center">
                  <h1
                    className="font-semibold ads:text-primary text-32 text-gray-12 leading-10"
                    data-testid="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_HEADING"
                    id={headingId}
                  >
                    Add your first files
                  </h1>
                  <p
                    className="ads:text-secondary text-20 text-gray-11"
                    data-testid="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_BODY"
                  >
                    You can upload more once you&apos;re in.
                  </p>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <div className="flex items-center gap-3">
                    <AnimatedOnboardingUploadAction
                      icon={<UploadIcon size="sm" />}
                      label="Upload files"
                      onClick={handleFilesClick}
                      palette="translate"
                      slotIndex={0}
                      testId="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_FILES"
                    />
                    <AnimatedOnboardingUploadAction
                      icon={<FolderIcon size="sm" />}
                      label="Upload folder"
                      onClick={handleFolderClick}
                      palette="smart-resize"
                      slotIndex={1}
                      testId="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_FOLDER"
                    />
                  </div>
                  {skipOnboardingUpload ? (
                    <button
                      className="rounded-4 px-3 py-1.5 ads:text-secondary text-14 text-gray-11 transition-opacity duration-300 ads:hover:text-primary hover:text-gray-12 focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8"
                      data-testid="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_SKIP"
                      onClick={handleSkip}
                      type="button"
                    >
                      Skip for now
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
);

WorkspaceAnimatedOnboardingUploadStep.displayName = 'WorkspaceAnimatedOnboardingUploadStep';
